import { inject, NgModule, provideAppInitializer } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { environment } from '../environments/environment';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireFunctionsModule } from '@angular/fire/compat/functions';
import { AngularFirestoreModule } from '@angular/fire/compat/firestore';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { QRCodeComponent } from 'angularx-qrcode';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatBadgeModule } from '@angular/material/badge';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatStepperModule } from '@angular/material/stepper';
import { MatNativeDateModule } from '@angular/material/core';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { EventsComponent } from './pages/events/events.component';
import { EventComponent } from './pages/event/event.component';
import { ErrorComponent } from './components/error/error.component';
import { ShoutoutsComponent } from './pages/event/components/shoutouts/shoutouts.component';
import { ShoutoutCreateComponent } from './components/shoutout-create/shoutout-create.component';
import { ReactiveFormsModule } from '@angular/forms';
import { ShoutoutConfirmedComponent } from './components/shoutout-confirmed/shoutout-confirmed.component';
import { EventFormComponent } from './pages/event-form/event-form.component';
import { LoginComponent } from './components/login/login.component';
import { SignupComponent } from './components/signup/signup.component';
import { HeaderComponent } from './components/header/header.component';
import { EventWallComponent } from './pages/event-wall/event-wall.component';
import { AnnounceComponent } from './pages/event/components/announce/announce.component';
import { EventQrComponent } from './pages/event-qr/event-qr.component';
import { ForgotPasswordComponent } from './components/forgot-password/forgot-password.component';
import { UserComponent } from './pages/user/user.component';
import { ShoutoutsActionsComponent } from './pages/event/components/shoutouts/components/shoutouts-actions/shoutouts-actions.component';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { provideAnalytics, getAnalytics } from '@angular/fire/analytics';
import { AuthService } from './services/auth.service';
import { firstValueFrom, switchMap } from 'rxjs';
import { EventHeaderComponent } from './pages/event/components/event-header/event-header.component';
import { EventBalanceComponent } from './pages/event/components/event-balance/event-balance.component';
import { KioskComponent } from './pages/kiosk/kiosk.component';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatChipsModule } from '@angular/material/chips';
import { EventBalancesTableComponent } from './pages/user/components/event-balances-table/event-balances-table.component';
import { AdminPayoutsTableComponent } from './pages/user/components/admin-payouts-table/admin-payouts-table.component';
import { OrganizerPayoutsTableComponent } from './pages/user/components/organizer-payouts-table/organizer-payouts-table.component';
import { EventDetailsStepComponent } from './pages/event-form/components/event-details-step.component';
import { PricingOptionsStepComponent } from './pages/event-form/components/pricing-options-step.component';
import { SessionsStepComponent } from './pages/event-form/components/sessions-step.component';
import { PaymentTypeStepComponent } from './pages/event-form/components/payment-type-step.component';
import { MatTooltipModule } from '@angular/material/tooltip';

@NgModule({
  declarations: [
    AppComponent,
    EventsComponent,
    EventComponent,
    ErrorComponent,
    ShoutoutsComponent,
    ShoutoutCreateComponent,
    ShoutoutConfirmedComponent,
    EventFormComponent,
    EventWallComponent,
    EventQrComponent,
    LoginComponent,
    SignupComponent,
    HeaderComponent,
    AnnounceComponent,
    ForgotPasswordComponent,
    UserComponent,
    ShoutoutsActionsComponent,
    EventHeaderComponent,
    EventBalanceComponent,
    KioskComponent,
    EventBalancesTableComponent,
    EventDetailsStepComponent,
    PricingOptionsStepComponent,
    SessionsStepComponent,
    PaymentTypeStepComponent,
  ],
  imports: [
    CommonModule,
    BrowserModule,
    ReactiveFormsModule,
    AppRoutingModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    AngularFireModule.initializeApp(environment.firebase),
    AngularFirestoreModule, //.enablePersistence(),
    AngularFireModule,
    AngularFireFunctionsModule,
    BrowserAnimationsModule,
    QRCodeComponent,
    MatTableModule,
    MatButtonModule,
    MatMenuModule,
    MatToolbarModule,
    MatIconModule,
    MatTabsModule,
    MatBadgeModule,
    MatFormFieldModule,
    MatInputModule,
    MatCardModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatStepperModule,
    MatSelectModule,
    MatDividerModule,
    MatSnackBarModule,
    MatRadioModule,
    MatChipsModule,
    MatTooltipModule,
    AdminPayoutsTableComponent,
    OrganizerPayoutsTableComponent,
  ],
  providers: [
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => getAuth()),
    provideFirestore(() => getFirestore()),
    provideAnalytics(() => getAnalytics()),
    provideAppInitializer(() => {
      const auth = inject(AuthService);
      return firstValueFrom(
        auth.initialize().pipe(switchMap((userDataChanges) => userDataChanges)),
      );
    }),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
